// Libs
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Wrapper } from "@googlemaps/react-wrapper";

// Local
import { DbRead, DbWrite } from "../../database";
import { ExistingVendor } from "../../models/vendor";
import { PURCHASE_ORDERS_URL, WORK_RECORD_AND_TASKS_URL } from "../../urls";
import { useAuthStore } from "../../store/firebase-auth";
import { convertJSDateToFSTimestamp } from "../../utils/convertJSDateToFSTimestamp";
import AddNewPurchaseOrderPage from "../../components/purchaseOrders/AddNewPurchaseOrderPage";
import { PurchaseOrderFormValues } from "../../components/purchaseOrders/AddNewPurchaseOrderForm";

interface Props {
  siteKey: string;
}

export default function NewPurchaseOrderContainer({ siteKey }: Props) {
  const [vendorList, setVendorList] = useState<ExistingVendor[]>([]);
  const firebaseUser = useAuthStore((state) => state.firebaseUser);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get craftRecordID from URL parameters
  const craftRecordID = searchParams.get("craftRecordID");
  const apiKey = import.meta.env.VITE_APP_GOOGLE_MAP_KEY;

  useEffect(() => {
    // Subscribe to all vendors so the list updates when new vendors are added
    const unsubscribe = DbRead.vendors.subscribeAll(siteKey, setVendorList);

    return () => unsubscribe && unsubscribe();
  }, [siteKey]);

  const handleSavePurchaseOrder = async (
    formValues: PurchaseOrderFormValues,
  ) => {
    try {
      if (!firebaseUser) throw Error("No Firebase user was found");

      // Convert Date objects to Firestore Timestamps
      const issueDate = convertJSDateToFSTimestamp(formValues.issueDate);
      const expectedDeliveryDate = formValues.expectedDeliveryDate
        ? convertJSDateToFSTimestamp(formValues.expectedDeliveryDate)
        : null;

      // Fix lineItems to ensure priceBookItemID is not undefined
      const lineItems = formValues.lineItems.map((item) => ({
        ...item,
        priceBookItemID: item.priceBookItemID ?? null,
        description: item.description ?? null,
        vendorPartNumber: item.vendorPartNumber ?? null,
      }));

      // Ensure nullable fields are properly handled and include craftRecordID
      const poData = {
        ...formValues,
        lineItems,
        issueDate,
        expectedDeliveryDate,
        siteKey: siteKey,
        createdBy: firebaseUser.uid,
        lastModifiedBy: firebaseUser.uid,
        deliveryAddressLine2: formValues.deliveryAddressLine2 ?? null,
        paymentTerms: formValues.paymentTerms ?? null,
        vendorReferenceNumber: formValues.vendorReferenceNumber ?? null,
        internalNotes: formValues.internalNotes ?? null,
        notes: formValues.notes || "",
        totalTaxAmount: formValues.totalTaxAmount ?? null,
        craftRecordID: craftRecordID, // Include the craftRecordID from URL params
      };

      await DbWrite.purchaseOrders.create(poData);

      return Promise.resolve();
    } catch (error) {
      console.error("Error creating purchase order:", error);
      return Promise.reject(error);
    }
  };

  const navigateBack = () => {
    // If we have a craftRecordID, navigate back to the job page
    if (craftRecordID) {
      navigate(`${WORK_RECORD_AND_TASKS_URL}/${craftRecordID}`);
    } else {
      // Otherwise, navigate to the purchase orders list
      navigate(PURCHASE_ORDERS_URL);
    }
  };

  return (
    <Wrapper apiKey={apiKey ?? ""} libraries={["places"]}>
      <AddNewPurchaseOrderPage
        siteKey={siteKey}
        vendorList={vendorList}
        craftRecordID={craftRecordID}
        handleSave={handleSavePurchaseOrder}
        navigateBack={navigateBack}
      />
    </Wrapper>
  );
}
